import React from 'react';
import {Grid, Box} from '@mui/material';
import {UploadDocumentReportPack} from './UploadDocumentReportPack';
import {ReportPackFragment} from '../../schema-types/ReportPackFragment';
import withAutoFormNew, {RecursivePartial} from '../../components/AutoForm/AutoFormNew';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';
import {
  ReportPackTemplateATRTypeEnum,
  ReportPackTemplateDocumentTypeEnum,
  ReportPackTemplateNewOrExistingClientEnum,
  ReportPackTemplateTypes,
} from '../../schema-types/globalTypes';
import {useAutoField} from '../../components/AutoForm/useAutoForm';
import {useReportPack} from './useReportPack';
import {useReviewContext} from '../../contexts/ReviewContext';
import {NoActionOptions} from './NoActionOptions';
import {ReportPackEditor} from './ReportPackEditor';
import {getReportPackLetterTitle} from './ReportPackHelpers';
import {DeclinedOptions} from './DeclinedOptions';
import {UncontactableOptions} from './UncontactableOptions';
import {UPDATES} from '../../shared/UPDATES';

type ReportPackFileProps = {
  reportPack: ReportPackFragment;
};

export const ReportPackFile: React.FC<ReportPackFileProps> = ({reportPack}) => {
  const initialValueOverrides: RecursivePartial<reportPackFileForm> = {
    type:
      reportPack.templateType === ReportPackTemplateTypes.ATTACHMENT_LETTER
        ? 'upload'
        : reportPack.templateType
        ? 'template'
        : null,
    // TODO(Nino): Add the correct initial values for the form
    // type: reportPack,
    templateType: reportPack.templateType || null,
    templateHtml: reportPack.templateHtml || null,
    noSignatureRequired:
      reportPack.templateType === ReportPackTemplateTypes.NO_ACTION_LETTER ||
      reportPack.templateType === ReportPackTemplateTypes.DECLINED_LETTER ||
      reportPack.templateType === ReportPackTemplateTypes.UNCONTACTABLE_LETTER,
  };
  return (
    <>
      <ReportPackFileFormWrapped
        reportPack={reportPack}
        formFields={[]}
        initialValueOverrides={initialValueOverrides}
      />
    </>
  );
};

type reportPackFileForm = {
  type: 'template' | 'upload' | null;
  templateType: ReportPackTemplateTypes | null;
  clientType: ReportPackTemplateNewOrExistingClientEnum | null;
  atrType: ReportPackTemplateATRTypeEnum | null;
  includeOptions: ReportPackTemplateDocumentTypeEnum[];
  templateHtml: string | null;
  noSignatureRequired?: boolean;
};

const ReportPackFileForm: React.FC<ReportPackFileProps> = ({reportPack}) => {
  const {reviewId} = useReviewContext();
  const [{value: type}] = useAutoField<reportPackFileForm['type']>('type');
  const [{value: reportHTML}, _, {setValue: settemplateHtml}] =
    useAutoField<reportPackFileForm['templateHtml']>('templateHtml');

  const {onUpsert} = useReportPack();
  return (
    <>
      {type !== 'upload' && (
        <>
          <Grid container spacing={1} mb={4}>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
              }}
            >
              <Panel
                title={`Generate ${getReportPackLetterTitle(reportPack.templateType)}`}
                color={themeColors.amber}
                style={{
                  display: 'flex',
                  flexGrow: 1,
                }}
                childrenContainerStyle={{
                  display: 'flex',
                  flexGrow: 1,
                  flexDirection: 'column',
                  width: '100%',
                  paddingBottom: '0', // Reduce bottom padding in Panel
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    {reportPack.templateType ===
                      ReportPackTemplateTypes.NO_ACTION_LETTER && (
                      <NoActionOptions
                        reportPack={reportPack}
                        onTemplateHtmlChange={(val) =>
                          settemplateHtml(UPDATES._2105, val)
                        }
                      />
                    )}
                    {reportPack.templateType ===
                      ReportPackTemplateTypes.DECLINED_LETTER && (
                      <DeclinedOptions
                        reportPack={reportPack}
                        onTemplateHtmlChange={(val) =>
                          settemplateHtml(UPDATES._2105, val)
                        }
                      />
                    )}
                    {reportPack.templateType ===
                      ReportPackTemplateTypes.UNCONTACTABLE_LETTER && (
                      <UncontactableOptions
                        reportPack={reportPack}
                        onTemplateHtmlChange={(val) =>
                          settemplateHtml(UPDATES._2105, val)
                        }
                      />
                    )}
                  </Grid>
                </Grid>
              </Panel>
            </Grid>
          </Grid>
          <ReportPackEditor
            templateType={reportPack.templateType || undefined}
            reportHTML={reportHTML || undefined}
            onSave={async (val) => {
              onUpsert({
                variables: {
                  reviewId,
                  reportPackId: reportPack.id,
                  templateHtml: val,
                  templateType: reportPack.templateType!,
                },
              });
            }}
          />
        </>
      )}
      {reportPack.templateType === ReportPackTemplateTypes.ATTACHMENT_LETTER && (
        <Box mb={4}>
          <UploadDocumentReportPack reportPack={reportPack} />
        </Box>
      )}
    </>
  );
};

const ReportPackFileFormWrapped = withAutoFormNew<
  reportPackFileForm,
  ReportPackFileProps
>(ReportPackFileForm, {});
